import * as THREE from "three";
import { Enemy, LevelSim } from "@highmarch/domain";
import type { UnitLook } from "../unitFx";
import { groundAt } from "../terrain";
import { HitStop } from "../../level/hitStop";
import type { UnitRings } from "./blobShadow";
import { BOSS, BossEvents, BossHandle, bossPhase, emerge, footfallCrossed, phasePop, telegraphProgress } from "./bossFx";

/** The optional outside beats a boss moment calls (all null-safe: a missing side is a quieter moment, never an error). */
export interface BossBeats {
  /** Sprite VFX dust (art/vfx.js `dust`); absent until the atlas loads. */
  dust: ((pos: THREE.Vector3, n: number, opts?: Record<string, unknown>) => void) | null;
  addTrauma: ((amount: number) => void) | null;
  hitStop: { request(ms: number, o?: { ramp?: boolean; force?: boolean }): boolean } | null;
  /** DOM screen flash (ui's `ScreenFlash.flash`, via FxHooks.screenFlash at merge). */
  screenFlash: ((color: string, peak: number, ms: number) => void) | null;
  /** Fly `n` reward coins from a world point into the HUD points pill (the level projects it; ui's `flyCoins`). */
  coinsTo: ((world: THREE.Vector3, n: number) => void) | null;
}

/** One boss view's moment state. `handle` is what listeners receive (kept, not rebuilt per event). */
export interface BossState {
  readonly handle: { id: number; archId: string; root: THREE.Object3D; body: THREE.Object3D; look: UnitLook | null };
  emergeT: number; stomped: boolean; phase: number; popT: number; footPrev: number; hpSent: number;
  /** Telegraph fill (−1 = none) and where the blow lands. */
  tele: number; teleX: number; teleZ: number;
  flashed: boolean; coinsSent: boolean; deathAt: THREE.Vector3;
}

const TELE = new THREE.Color(0xff3a22);
const _p = new THREE.Vector3();

/**
 * Runs the boss hero moments (bossFx.ts has the curves and the story) for `EnemyViews`: arrival, footfalls,
 * telegraphs, the 50 % phase change and the fall. Pure presentation — it reads the sim, never writes it.
 */
export class BossStage {
  constructor(private readonly sim: LevelSim, readonly events: BossEvents, private readonly beats: BossBeats, private readonly rings: UnitRings) {}

  create(root: THREE.Object3D, body: THREE.Object3D, look: UnitLook | null): BossState {
    return {
      handle: { id: 0, archId: "", root, body, look },
      emergeT: 0, stomped: false, phase: 1, popT: 99, footPrev: 0, hpSent: -1, tele: -1, teleX: 0, teleZ: 0,
      flashed: false, coinsSent: false, deathAt: new THREE.Vector3(),
    };
  }

  /**
   * A boss appears: reset its moment, dip the clock, tell the listeners. `rise` false skips the climb out of the ground
   * and its stomp (the Starfall Wyrm dives in from the sky with its own clip, M40).
   */
  spawn(s: BossState, id: number, archId: string, reduced: boolean, rise = true): void {
    s.handle.id = id; s.handle.archId = archId;
    s.emergeT = rise ? 0 : BOSS.emergeSeconds; s.stomped = !rise; s.phase = 1; s.popT = 99; s.footPrev = 0; s.hpSent = -1; s.tele = -1;
    s.flashed = false; s.coinsSent = false;
    if (!reduced) this.beats.hitStop?.request(BOSS.dipMs, { ramp: true, force: true });
    this.events.spawn(s.handle);
  }

  /** Per sim tick while alive: the health fraction for the bar, the 50 % phase change, the strike telegraph. */
  tick(s: BossState, e: Enemy, frac: number, reduced: boolean): void {
    if (Math.abs(frac - s.hpSent) > 0.001) { s.hpSent = frac; this.events.hp(frac, s.handle); }
    const phase = bossPhase(frac);
    if (phase > s.phase) {
      s.phase = phase;
      s.popT = 0;
      const look = s.handle.look;
      if (look) { look.heatUp(1); look.flash(reduced ? 0.5 : 1); look.rimStrength += BOSS.phaseRim; }
      if (!reduced) { this.beats.hitStop?.request(BOSS.phaseHitStopMs, { force: true }); this.beats.addTrauma?.(BOSS.phaseTrauma); }
      this.events.phase(phase, s.handle);
    }
    s.tele = telegraphProgress(e.attackCooldown, Math.min(BOSS.telegraphTicks, this.sim.balance.economy.troops.enemyAttackIntervalTicks));
    if (s.tele >= 0) this.target(s, e);
  }

  /** The blocked boss struck the troop holding it: dust where it landed and a jolt. */
  struck(s: BossState, e: Enemy, reduced: boolean): void {
    this.target(s, e);
    _p.set(s.teleX, groundAt(s.teleX, s.teleZ) + 0.04, s.teleZ);
    this.beats.dust?.(_p, 8, { size: 0.45, speed: 1.4, color: [0.62, 0.52, 0.42], life: 0.6 });
    if (!reduced) this.beats.addTrauma?.(0.15);
    s.tele = -1;
  }

  /**
   * Per frame while alive. Returns the boss's size multiplier (arrival and phase pop) and sets `rise`, the vertical
   * offset of the arrival in world units. `walkPhase` is the walk loop's 0–1 phase (NaN when not walking).
   */
  frame(s: BossState, dt: number, height: number, walkPhase: number, reduced: boolean, out: { rise: number; scale: number }): void {
    s.emergeT += dt;
    s.popT += dt;
    const root = s.handle.root.position;
    if (s.emergeT < BOSS.emergeSeconds) {
      const m = emerge(s.emergeT);
      out.rise = m.rise * height; out.scale = m.scale;
    } else { out.rise = 0; out.scale = 1; }
    out.scale *= 1 + phasePop(s.popT);
    if (!s.stomped && s.emergeT >= BOSS.stompAt) {
      s.stomped = true;
      this.beats.dust?.(_p.set(root.x, root.y + 0.03, root.z), 14, { size: 0.8, speed: 2.2, color: [0.6, 0.5, 0.4], life: 0.8 });
      if (!reduced) this.beats.addTrauma?.(BOSS.stompTrauma);
    }
    // heavy footfalls once it is out of the ground
    if (s.emergeT >= BOSS.emergeSeconds && !Number.isNaN(walkPhase)) {
      if (footfallCrossed(s.footPrev, walkPhase)) {
        this.beats.dust?.(_p.set(root.x, root.y + 0.03, root.z), 4, { size: 0.4, speed: 1.0, color: [0.6, 0.52, 0.44], life: 0.5 });
        if (!reduced) this.beats.addTrauma?.(BOSS.heavy.has(s.handle.archId) ? BOSS.footTrauma.heavy : BOSS.footTrauma.other);
      }
      s.footPrev = walkPhase;
    }
    if (s.tele >= 0) this.rings.telegraph(s.teleX, groundAt(s.teleX, s.teleZ), s.teleZ, BOSS.telegraphWidth, TELE, s.tele);
  }

  /** Rune-ring strength during the arrival (it fades in as the boss rises). */
  ringFade(s: BossState): number { return Math.min(1, s.emergeT / (BOSS.emergeSeconds * 0.5)); }

  /** The boss dies: 250 ms hit-stop, a white-hot rim, the listeners (with the feet position). */
  death(s: BossState): void {
    s.deathAt.copy(s.handle.root.position);
    s.tele = -1;
    s.flashed = false; s.coinsSent = false;
    s.handle.look?.heatUp(1);
    this.beats.hitStop?.request(HitStop.Ms.bossDeath, { ramp: true, force: true });
    this.events.death(s.handle, s.deathAt.clone());
  }

  /** The boss left without a kill (leaked into the fortress): no fall, but the listeners let go of it. */
  gone(s: BossState): void {
    s.tele = -1;
    this.events.gone(s.handle);
  }

  /** Per frame while it falls, `dead` seconds in: cracks, the burst's screen flash, then the coins to the HUD. */
  dieFrame(s: BossState, dead: number, height: number, reduced: boolean): void {
    const look = s.handle.look;
    if (look) {
      look.crack = dead / BOSS.crackSeconds;
      if (dead < BOSS.crackSeconds) look.heatUp(1);
    }
    if (!s.flashed && dead >= BOSS.crackSeconds) {
      s.flashed = true;
      if (!reduced) this.beats.screenFlash?.(BOSS.flash.color, BOSS.flash.peak, BOSS.flash.ms);
    }
    if (!s.coinsSent && dead >= BOSS.coinsAt) {
      s.coinsSent = true;
      this.beats.coinsTo?.(_p.copy(s.deathAt).setY(s.deathAt.y + height * 0.5), BOSS.coins);
    }
  }

  /** Where a blocked boss's blow lands: the nearest living troop (it is the one holding the boss). */
  private target(s: BossState, e: Enemy): void {
    const ex = e.x.toNumber(), ez = e.y.toNumber();
    let best = Infinity;
    s.teleX = ex; s.teleZ = ez;
    for (const t of this.sim.troops.slots) {
      if (!t.alive) continue;
      const tx = t.x.toNumber(), tz = t.y.toNumber(), d = (tx - ex) * (tx - ex) + (tz - ez) * (tz - ez);
      if (d < best) { best = d; s.teleX = tx; s.teleZ = tz; }
    }
  }
}

export type { BossHandle };
